import React from "react";
import StyledTooltip from "../../common/StyledTooltip";

const SkeletonBox = ({ w, h, style = {} }) => (
  <div style={{
    width: w, height: h, borderRadius: "6px",
    background: "linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)",
    backgroundSize: "600px 100%",
    animation: "shimmer 1.4s infinite linear",
    ...style,
  }} />
);

const shimmerKeyframes = `@keyframes shimmer{0%{background-position:-600px 0}100%{background-position:600px 0}}`;

export default function FinancialStatementTable({ data, loading = false }) {
  const rows = [
    {
      name: "Interest Income",
      amount: data?.financialBreakdown?.interest?.display || "₹0",
      originalValue: data?.financialBreakdown?.interest?.amount || 0,
      percent: data?.financialBreakdown?.interest?.percentage + "%" || "0%",
      tag: "Lending", color: "#008236", bg: "#DCFCE7", textColor: "#00A63E",
    },
    {
      name: "Processing Fee",
      amount: data?.financialBreakdown?.processingFee?.display || "₹0",
      originalValue: data?.financialBreakdown?.processingFee?.amount || 0,
      percent: data?.financialBreakdown?.processingFee?.percentage + "%" || "0%",
      tag: "Upfront", color: "#A65F00", bg: "#FFE2E2", textColor: "#D08700",
    },
    {
      name: "Processing Fee Without GST",
      amount: data?.financialBreakdown?.processingFeeWithoutGst?.display || "₹0",
      originalValue: data?.financialBreakdown?.processingFeeWithoutGst?.amount || 0,
      percent: data?.financialBreakdown?.processingFeeWithoutGst?.percentage + "%" || "0%",
      tag: "Upfront", color: "#A65F00", bg: "#FFE2E2", textColor: "#D08700",
    },
    {
      name: "Charges",
      amount: data?.financialBreakdown?.charges?.display || "₹0",
      originalValue: data?.financialBreakdown?.charges?.amount || 0,
      percent: data?.financialBreakdown?.charges?.percentage + "%" || "0%",
      tag: "Other", color: "#C10007", bg: "#FEF9C2", textColor: "#E7000B",
    },
  ];

  const totalAmount =
    (data?.financialBreakdown?.interest?.amount || 0) +
    (data?.financialBreakdown?.processingFee?.amount || 0) +
    (data?.financialBreakdown?.charges?.amount || 0);
  const totalPercentAmount =
    (data?.financialBreakdown?.interest?.percentage || 0) +
    (data?.financialBreakdown?.processingFee?.percentage || 0) +
    (data?.financialBreakdown?.charges?.percentage || 0);
  const totalDisplay = `₹${(totalAmount / 100000).toFixed(2)}L`;

  const isLarge = window.innerWidth > 1800;

  return (
    <>
      <style>{shimmerKeyframes}</style>
      <div style={{
        background: "#ffffff", border: "0.8px solid #E5E7EB", borderRadius: "12px",
        padding: "22px", marginTop: isLarge ? "30px" : "20px",
        boxShadow: "0px 1px 2px rgba(0,0,0,0.10), 0px 1px 3px rgba(0,0,0,0.10)",
      }}>
        {loading ? (
          <>
            <SkeletonBox w="220px" h="22px" style={{ marginBottom: "18px" }} />
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #D1D5DC" }}>
                  <th style={{ padding: "12px 20px", textAlign: "left" }}><SkeletonBox w="130px" h="14px" /></th>
                  <th style={{ padding: "12px", textAlign: "center" }}><SkeletonBox w="60px" h="14px" style={{ margin: "0 auto" }} /></th>
                  <th style={{ padding: "12px", textAlign: "center" }}><SkeletonBox w="60px" h="14px" style={{ margin: "0 auto" }} /></th>
                  <th style={{ padding: "12px", textAlign: "right" }}><SkeletonBox w="70px" h="14px" style={{ marginLeft: "auto" }} /></th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i} style={{ borderBottom: "0.8px solid #F3F4F6" }}>
                    <td style={{ padding: "14px 20px" }}><SkeletonBox w="140px" h="14px" /></td>
                    <td style={{ padding: "14px", textAlign: "center" }}><SkeletonBox w="70px" h="18px" style={{ margin: "0 auto", borderRadius: "4px" }} /></td>
                    <td style={{ padding: "14px", textAlign: "center" }}><SkeletonBox w="45px" h="14px" style={{ margin: "0 auto" }} /></td>
                    <td style={{ padding: "14px", textAlign: "right" }}><SkeletonBox w="60px" h="24px" style={{ borderRadius: "8px", marginLeft: "auto" }} /></td>
                  </tr>
                ))}
                <tr style={{ background: "#f7f8fb", borderTop: "2px solid #D1D5DC" }}>
                  <td style={{ padding: "14px 20px" }}><SkeletonBox w="100px" h="16px" /></td>
                  <td style={{ padding: "14px", textAlign: "center" }}><SkeletonBox w="80px" h="20px" style={{ margin: "0 auto", borderRadius: "4px" }} /></td>
                  <td style={{ padding: "14px", textAlign: "center" }}><SkeletonBox w="45px" h="14px" style={{ margin: "0 auto" }} /></td>
                  <td />
                </tr>
              </tbody>
            </table>
          </>
        ) : (
          <>
            <div style={{ fontSize: "20px", fontWeight: "600", marginBottom: "18px", color: "#0A0A0A" }}>
              Financial MIS Statement
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ color: "#0A0A0A", borderBottom: "2px solid #D1D5DC", fontWeight: "500" }}>
                  <th style={thStyleLeft}>Income Component</th>
                  <th style={thStyleCenter}>Amount</th>
                  <th style={thStyleCenter}>% of Total</th>
                  <th style={thStyleRight}>Category</th>
                </tr>
              </thead>
              <tbody>
                {rows?.map((row, i) => (
                  <tr key={i} style={{ borderBottom: "0.8px solid #F3F4F6" }}>
                    <td style={tdLeft}>{row?.name}</td>
                    <td style={{ ...tdCenter, color: row?.textColor, fontWeight: "700", fontSize: "16px" }}>
                      <StyledTooltip value={row?.originalValue}>{row?.amount}</StyledTooltip>
                    </td>
                    <td style={tdCenter}>{row?.percent}</td>
                    <td style={tdRight}>
                      <span style={{ background: row?.bg, color: row?.color, padding: "4px 10px", borderRadius: "8px", fontSize: isLarge ? "14px" : "12px", fontWeight: "600" }}>
                        {row.tag}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr style={{ background: "#f7f8fb", fontWeight: "600", borderTop: "2px solid #D1D5DC" }}>
                  <td style={tdTotal}>Total Income</td>
                  <td style={{ ...tdCenter, color: "#9810FA", fontWeight: "700", fontSize: "18px" }}>
                    <StyledTooltip value={totalAmount}>{totalDisplay}</StyledTooltip>
                  </td>
                  <td style={tdCenter}>{totalPercentAmount.toFixed(2) ?? 0}%</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  );
}

const isLarge = window.innerWidth > 1800;
const thStyleLeft = { textAlign: "left", paddingLeft: "20px", fontWeight: "700", color: "#0A0A0A", fontSize: isLarge ? "18px" : "16px" };
const thStyleCenter = { textAlign: "center", padding: "20px 0", fontSize: isLarge ? "18px" : "16px", color: "#0A0A0A", fontWeight: "700" };
const thStyleRight = { textAlign: "right", padding: "10px 0", fontSize: isLarge ? "18px" : "16px", color: "#0A0A0A", fontWeight: "700" };
const tdLeft = { padding: "14px 20px", color: "#0A0A0A", fontWeight: "500", fontSize: isLarge ? "18px" : "16px" };
const tdTotal = { padding: "14px 20px", color: "#111827", fontWeight: "700", fontSize: "18px", borderTop: "1px solid #111827" };
const tdCenter = { textAlign: "center", padding: "14px 0", color: "#0A0A0A", fontWeight: "400", fontSize: isLarge ? "18px" : "16px" };
const tdRight = { textAlign: "right", padding: "14px 0", fontSize: isLarge ? "16px" : "14px", fontWeight: "400" };